import React, { useEffect, useState } from "react";
import { Box, Card, Chip, Button, useMediaQuery } from "@mui/material";
import { useNavigate } from "react-router-dom";

import {
  CustomDataTable,
  TableColumn,
} from "../../../components/CommnTable/CustomDataTable";
import { useSearchParams } from "react-router-dom";
import { configNotToken, GetUrl, PostUrl } from "../../../api/endpointConfig";
import { useGet } from "../../../services/hooks/useGet";
import { useLoader } from "../../../Loader/LoaderContext";
import { useDispatch, useSelector } from "react-redux";
import { addScholarshipSummaryItem } from "../../../redux/actions/Actions";
import { ColumnGroup } from "primereact/columngroup";
import { Row } from "primereact/row";
import { Column } from "primereact/column";
import StatusUpdateModal from "../../Applications/StatusUpdateModal/StatusUpdateModal";
import { statusOptions, summaryStatusOptions } from "../../../data/alldata";
import { ScholarshipSummaryType } from "../../../types/types";
import { useMutationByMethod } from "../../../services/hooks/useMutationByMethod";
import { RootState } from "../../../redux/store";

const getStatusChipColor = (status: string) => {
  switch (status?.toLowerCase()) {
    case "completed":
      return "success";
    case "on_hold":
      return "warning";
    case "cancelled":
      return "error";
    case "active":
      return "info";
    default:
      return "default";
  }
};

const ScholarshipSummary: React.FC = () => {
  const [searchParams] = useSearchParams();

  const navigate = useNavigate();
  const [offsetValue, setOffsetValue] = useState<number>(0);
  const [limitValue, setLimitValue] = useState<number>(20);
  const [mainData, setMainData] = useState<ScholarshipSummaryType[]>([]);
  const [selectedRow, setSelectedRow] = useState<ScholarshipSummaryType[]>([]);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  // const [clearFilterState, setClearFilterState] = useState<boolean>(false);
  const [statusArr, setStatusArr] = useState<any[]>([]);
  const { showLoader, hideLoader } = useLoader();
  const dispatch = useDispatch();
  const [queryParams, setQueryParams] = useState<any>({});

  const scholarshipSummaryArr = useSelector(
    (state: RootState) => state.app.ScholarshipSummaryDetails
  );
  const pagination: Record<string, string | number | null> = {
    offset: 0,
    limit: 0,
  };

  const isDesktop = useMediaQuery("(min-width:1025px)");

  useEffect(() => {
    const prefix = searchParams.get("prefix");
    const text = searchParams.get("text");
    setQueryParams((prev: any) => {
      const updated = { ...prev };

      if (text && prefix) {
        updated[prefix] = text;
      } else {
        // setQueryParams({})
      }
      return updated;
    });
  }, [searchParams]);
  const setClearFilterState = (state: boolean) => {
    console.log("Clear filter state in parent:", state);
    if (state) {
      setQueryParams({});
    }
  };

  const getApplicationData = useGet<any>(`${GetUrl.getScholarshipSummary}/`, {
    params: { ...pagination, ...queryParams },
  });

  useEffect(() => {
    fetchApplicationData();
  }, [queryParams]);

  const fetchApplicationData = async () => {
    showLoader();
    try {
      const result = await getApplicationData.refetch();
      hideLoader();
      if (result?.data) {
        setMainData(result.data);
      }
    } catch (error) {
      hideLoader();
      console.error("Error fetching app version:", error);
    } finally {
      hideLoader();
    }
  };
  
  const columns: TableColumn<ScholarshipSummaryType>[] = [
    {
      field: "scholarship_id",
      header: "Scholarship ID",
      width: "10rem",
      frozen: isDesktop ? true : false,
      alignFrozen: "left",
      style: { whiteSpace: "nowrap" },
      sortable: true,
      filterable: true,
      customBody: (rowData) => (
        <span
          style={{
            color: "#1976d2",
            cursor: "pointer",
            textDecoration: "underline",
          }}
          onClick={() => {
            dispatch(addScholarshipSummaryItem(rowData) as any);
            navigate(`/scholarship-summary/${rowData.user_id}`);
          }}
        >
          {rowData.scholarship_id}
        </span>
      ),
    },
    {
      field: "name",
      header: "Name",
      sortable: true,
      filterable: true,
      style: { whiteSpace: "nowrap", textTransform: "capitalize" },
    },
    {
      field: "course",
      header: "Course",
      sortable: true,
      filterable: true,
      style: { whiteSpace: "nowrap" },
    },
    {
      field: "stream",
      header: "Stream",
      sortable: true,
      filterable: true,
      style: { whiteSpace: "nowrap" },
    },
    {
      field: "status",
      header: "Status",
      sortable: true,
      filterable: true,
      style: { whiteSpace: "nowrap" },
      customBody: (rowData) =>
        rowData.status ? (
          <Chip
            label={rowData.status}
            color={getStatusChipColor(rowData.status)}
            size="small"
            variant="filled"
            style={{ fontWeight: 600, textTransform: "capitalize" }}
          />
        ) : null,
    },
    {
      field: "college_school",
      header: "College/School",
      sortable: true,
      filterable: true,
      style: { whiteSpace: "nowrap" },
    },
    {
      field: "commitment_period",
      header: "Commitment Period",
      sortable: true,
      style: { whiteSpace: "nowrap" },
    },
    {
      field: "amount_committed_per_year",
      header: "Amount Committed per Year",
      sortable: true,
      style: { whiteSpace: "nowrap" },
    },
    {
      field: "batch",
      header: "Batch",
      sortable: true,
      style: { whiteSpace: "nowrap" },
    },
    {
      field: "amount_paid_till_date",
      header: "Amount Paid",
      sortable: true,
      style: { whiteSpace: "nowrap" },
    },
    {
      field: "next_due_installments",
      header: "Next Due",
      filterable: true,
      sortable: true,
      style: { whiteSpace: "nowrap" },
    },
    {
      field: "next_installment_amount",
      header: "Next Installment",
      sortable: true,
      style: { whiteSpace: "nowrap" },
    },
    {
      field: "balance_funds_required_till_date",
      header: "Balance Fund",
      sortable: true,
      style: { whiteSpace: "nowrap" },
    },
    {
      field: "total_fund_approval",
      header: "Total Fund",
      sortable: true,
      style: { whiteSpace: "nowrap" },
    },
    {
      field: "continue_funding_till",
      header: "Continue Till",
      sortable: true,
      style: { whiteSpace: "nowrap" },
    },
    {
      field: "status_update",
      header: "Status Update",
      sortable: false,
      frozen: isDesktop ? true : false,
      alignFrozen: isDesktop ? "right" : undefined,
      style: { whiteSpace: "nowrap" },
      customBody: (rowData) => (
        <Button
          size="small"
          variant="contained"
          color="success"
          className="commonBtnOne"
          onClick={() => {
            setSelectedRow([rowData]);
            setModalOpen(true);
          }}
        >
          Update Status
        </Button>
      ),
    },
    {
      field: "actions" as keyof ScholarshipSummaryType,
      header: "Actions",
      sortable: false,
      frozen: isDesktop ? true : false,
      alignFrozen: isDesktop ? "right" : undefined,
      style: { whiteSpace: "nowrap" },
      customBody: (rowData) =>
        rowData.scholarship_id !== "Total" ? (
          <Box style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
            <Button
              size="small"
              variant="contained"
              color="info"
              className="commonBtnTwo"
              onClick={() => {
                // const encodedId = encodeURIComponent(rowData.scholarship_id);
                dispatch(addScholarshipSummaryItem(rowData) as any);
                navigate(
                  `/scholarship-summary/${rowData.user_id}/scholarshipfund`
                );
              }}
            >
              Manage Fund
            </Button>
            {/* <Button size="small" variant="contained" color="success">
              Complete
            </Button>
            <Button size="small" variant="contained" color="warning">
              On Hold
            </Button>
            <Button size="small" variant="contained" color="error">
              Cancel
            </Button> */}
          </Box>
        ) : null,
    },
  ];
  const formatCurrency = (value: any) => {
    return value.toLocaleString("en-IN", {
      style: "currency",
      currency: "INR",
    });
  };
  const amountCommitted = () => {
    let total = 0 as any;
    for (let sale of mainData) {
      total += sale.amount_committed_per_year;
    }
    return formatCurrency(total);
  };
  const amountPaid = () => {
    let total = 0 as any;
    for (let sale of mainData) {
      total += sale.amount_paid_till_date;
    }
    return formatCurrency(total);
  };
  const nextAmount = () => {
    let total = 0 as any;
    for (let sale of mainData) {
      total += sale.next_installment_amount;
    }
    return formatCurrency(total);
  };
  const balanceFunds = () => {
    let total = 0 as any;
    for (let sale of mainData) {
      total += sale.balance_funds_required_till_date;
    }
    return formatCurrency(total);
  };
  const totalFund = () => {
    let total = 0 as any;
    for (let sale of mainData) {
      total += sale.total_fund_approval;
    }
    return formatCurrency(total);
  };

  const footerGroup = (
    <ColumnGroup>
      <Row>
        <Column footer="Totals:" frozen={isDesktop ? true : false} alignFrozen="left" />
        <Column colSpan={6} />
        <Column footer={amountCommitted} colSpan={2} />
        <Column footer={amountPaid} colSpan={2} />
        <Column footer={nextAmount} />
        <Column footer={balanceFunds} />
        <Column footer={totalFund} />
        <Column />
        <Column
          colSpan={3}
          frozen={isDesktop ? true : false}
          alignFrozen={isDesktop ? "right" : undefined}
        />
      </Row>
    </ColumnGroup>
  );

  // const fetchSummaryData = async () => refetch();

  const statusSubmited = (values: any) => {
    console.log("values==", values);
    setStatusArr([values]);
    setModalOpen(false);
    statusSubmitedCall(values);
  };
  const updateAppDetailsStatus = useMutationByMethod<any>(
    "post",
    `${PostUrl.scholarshipStatus}/${selectedRow[0]?.id}/status?status=${statusArr[0]?.status}&remarks=${statusArr[0]?.remarks}`
  );

  const statusSubmitedCall = (values: any) => {
    updateAppDetailsStatus.mutate(null, {
      onSuccess: (data) => {
        console.log("data new===", data);
        hideLoader();
        // fetchSummaryData();
        fetchApplicationData();
      },
      onError: (err) => {
        console.error("error", err);
        hideLoader();
      },
    });
  };
  const handlePageChange = (event: any) => {
    console.log("Pagination event:", event);

    // 🔁 You can make a paginated API call here:
    // fetchData({ page: event.page, rows: event.rows });

    // Example: Update local pagination state if needed
    // setCurrentPage(event.page);
    setOffsetValue(event.page * event.rows); // event.page usually gives pageIndex
    setLimitValue(event.rows);
  };
  return (
    <>
      <Card
        style={{
          padding: "1rem",
          border: 0,
          boxShadow: "none",
          background: "none",
        }}
      >
        <Box className="cardBoxUI">
          <CustomDataTable<ScholarshipSummaryType>
            data={mainData}
            columns={columns}
            tableTitle="Scholarship Summary Review"
            rowsPerPage={limitValue}
            first={offsetValue} // 🔑 Add this so DataTable syncs with state
            rows={limitValue}
            footerGroup={footerGroup}
            exportCSVShow={false}
            exportExcelShow={true}
            onSearchClear={() => setQueryParams({})}
            onPageChange={handlePageChange}
            clearFilter={setClearFilterState}
          />
        </Box>
      </Card>
      <StatusUpdateModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSubmit={(values) => {
          console.log("Form Values", values);
          statusSubmited(values);
        }}
        statusOptions={summaryStatusOptions.filter(
          (item: any) => item.value !== "approved"
        )}
        initialStatus={selectedRow[0]?.status}
      />
    </>
  );
};

export default ScholarshipSummary;
